import {
  instantToLocalWallTime,
  minutesToOffsetString,
  zoneOffsetMinutesAt,
} from '../../../components/payrollEdit/clockParse';

/**
 * Employee Time Portal time helpers. Every time the portal shows or sends is in the
 * TENANT zone (config.employeePortal.timeZone), never the browser's: an office PC set to
 * another zone must still stamp `-07:00` in Los Angeles. Built on the payroll editor's
 * clockParse helpers (zoneOffsetMinutesAt, the SWITCHOFFSET convention). Pure: the zone is
 * always an argument, so jest needs no config.
 */

/** The `[+-]HH:MM` offset of `zone` at an instant (DST-correct for that instant). */
export function offsetAt(instantMs: number, zone: string): string {
  return minutesToOffsetString(zoneOffsetMinutesAt(instantMs, zone));
}

/** A tap time → the `{ iso, offset }` pair a punch body carries (UTC ISO + tenant offset). */
export function stampInstant(instantMs: number, zone: string): { iso: string; offset: string } {
  return { iso: new Date(instantMs).toISOString(), offset: offsetAt(instantMs, zone) };
}

/** `YYYY-MM-DD` of an instant in `zone`. */
export function localYmd(instantMs: number, zone: string): string {
  const wall = instantToLocalWallTime(new Date(instantMs).toISOString(), offsetAt(instantMs, zone));
  return wall ? wall.ymd : '';
}

/** Minutes after local midnight of an instant in `zone`. */
export function localMinutes(instantMs: number, zone: string): number {
  const wall = instantToLocalWallTime(new Date(instantMs).toISOString(), offsetAt(instantMs, zone));
  return wall ? wall.minutes : 0;
}

/** `7:58 AM` in `zone`. */
export function fmtTime(instantMs: number, zone: string): string {
  return new Date(instantMs).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: zone });
}

/** `Mon, Oct 12` in `zone` (for an open punch from an earlier day). */
export function fmtDayShort(instantMs: number, zone: string): string {
  return new Date(instantMs).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: zone });
}

/** `Thursday, July 30, 2026` in `zone`. */
export function fmtDateLong(instantMs: number, zone: string): string {
  return new Date(instantMs).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: zone,
  });
}

/** A calendar date (`YYYY-MM-DD`, or an ISO date-time whose date part is the calendar day) → `Oct 13`. */
export function fmtYmdShort(ymd: string): string {
  const d = new Date(`${ymd.slice(0, 10)}T00:00:00Z`);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** `Fri Oct 16` for a calendar date. */
export function fmtYmdDow(ymd: string): string {
  const d = new Date(`${ymd.slice(0, 10)}T00:00:00Z`);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** Month (`OCT`) and day (`13`) of a calendar date, for the Coming up date tiles. */
export function ymdTile(ymd: string): { mon: string; day: string } {
  const d = new Date(`${ymd.slice(0, 10)}T00:00:00Z`);
  if (isNaN(d.getTime())) return { mon: '', day: '' };
  return {
    mon: d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' }).toUpperCase(),
    day: String(d.getUTCDate()),
  };
}

/** `YYYY-MM-DD` + n calendar days. */
export function addDaysYmd(ymd: string, days: number): string {
  const d = new Date(`${ymd.slice(0, 10)}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Minutes → decimal hours with two places (`6.53`), the portal's hour format (hundredths rounded half up, as allocateHundredths). */
export function fmtHours(minutes: number): string {
  return (Math.round((Math.max(0, minutes) * 100) / 60) / 100).toFixed(2);
}

/** An instant floored to its minute — punches are stored in whole minutes, like mobile (ClockManager.getPunchTime). */
export function floorToMinute(instantMs: number): number {
  return Math.floor(instantMs / 60000) * 60000;
}

/** The current time in `zone` as the Today clock parts `2:47` + `PM` (no seconds — punches are whole minutes). */
export function clockParts(instantMs: number, zone: string): { hm: string; ampm: string } {
  const [hm, ampm = ''] = fmtTime(instantMs, zone).split(/\s+/);
  return { hm, ampm };
}

/** `Good morning` / `Good afternoon` / `Good evening` for the local hour. */
export function greetingFor(localMinutesOfDay: number): string {
  if (localMinutesOfDay < 12 * 60) return 'Good morning';
  if (localMinutesOfDay < 17 * 60) return 'Good afternoon';
  return 'Good evening';
}
